import { createSystem, defaultConfig, defineRecipe, defineConfig } from '@chakra-ui/react';
import useWorkspace from '@/lib/hooks/use-workspace';
import { useEffect, useMemo } from 'react';
import {
  getContrastColor,
  adjustColorLightness,
  hexToRgba,
  normalizeHex,
} from '@/lib/color-utils';
import { dsFoundationConfig } from '@/lib/theme-ds';
import { dsRecipesConfig } from '@/lib/theme-recipes';

export const containerRecipe = defineRecipe({
  base: {
    px: { base: 0, md: '6', lg: '8' },
  },
});

const defaultColors = {
  primary: '#F5A623',
  secondary: '#f7f5f2',
};

/**
 * The themeable subset of the DS color tokens (design-system/tokens/colors.css
 * marks these "THEMEABLE (per-workspace)"; --ring is documented there as
 * "focus ring uses brand"). The DS stylesheet holds the Athlete Era default at
 * :root; a workspace with its own primary_color overrides them on the root
 * element so every `var(--brand-*)` consumer — the primary/chip button fill,
 * every focus ring, --info — tracks the workspace instead of the frozen orange.
 *
 * --brand-on-primary is deliberately NOT derived: the DS specifies white on the
 * brand fill for every workspace, and `brand.contrast` already forwards to it.
 */
const THEMEABLE_BRAND_VARS = [
  '--brand-primary',
  '--brand-primary-hover',
  '--brand-primary-soft',
  '--ring',
] as const;

export function useWorkspaceTheme() {
  const { workspace } = useWorkspace();

  const workspaceColors = useMemo(() => {
    return {
      primary: normalizeHex(workspace?.primary_color) || defaultColors.primary,
      secondary: normalizeHex(workspace?.secondary_color) || defaultColors.secondary,
    };
  }, [workspace?.primary_color, workspace?.secondary_color]);

  // Keep the DS custom properties in sync with the workspace. The derived stops
  // are the same ones the Chakra ramp below uses (500 rest / 600 hover / 50
  // soft) so the two systems cannot drift apart. When the workspace has no
  // valid color we remove the overrides entirely and the DS defaults — which
  // are hand-tuned, not ramp-derived — take over again.
  useEffect(() => {
    const root = document.documentElement;
    const custom = normalizeHex(workspace?.primary_color);
    const ring = custom && hexToRgba(custom, 0.35);

    if (!custom || !ring) {
      THEMEABLE_BRAND_VARS.forEach((cssVar) => root.style.removeProperty(cssVar));
      return;
    }

    root.style.setProperty('--brand-primary', custom);
    root.style.setProperty('--brand-primary-hover', adjustColorLightness(custom, -0.1));
    root.style.setProperty('--brand-primary-soft', adjustColorLightness(custom, 0.9));
    root.style.setProperty('--ring', ring);
  }, [workspace?.primary_color]);

  const primaryContrastColor = useMemo(
    () => getContrastColor(workspaceColors.primary),
    [workspaceColors.primary],
  );

  const secondaryContrastColor = useMemo(
    () => getContrastColor(workspaceColors.secondary),
    [workspaceColors.secondary],
  );

  const customConfig = useMemo(
    () =>
      defineConfig({
        theme: {
          recipes: {
            container: containerRecipe,
          },
          tokens: {
            colors: {
              brand: {
                50: { value: adjustColorLightness(workspaceColors.primary, 0.9) },
                100: { value: adjustColorLightness(workspaceColors.primary, 0.8) },
                200: { value: adjustColorLightness(workspaceColors.primary, 0.6) },
                300: { value: adjustColorLightness(workspaceColors.primary, 0.4) },
                400: { value: adjustColorLightness(workspaceColors.primary, 0.2) },
                500: { value: workspaceColors.primary },
                600: { value: adjustColorLightness(workspaceColors.primary, -0.1) },
                700: { value: adjustColorLightness(workspaceColors.primary, -0.2) },
                800: { value: adjustColorLightness(workspaceColors.primary, -0.3) },
                900: { value: adjustColorLightness(workspaceColors.primary, -0.4) },
              },
              secondary: {
                50: { value: adjustColorLightness(workspaceColors.secondary, 0.9) },
                100: { value: adjustColorLightness(workspaceColors.secondary, 0.8) },
                200: { value: adjustColorLightness(workspaceColors.secondary, 0.6) },
                300: { value: adjustColorLightness(workspaceColors.secondary, 0.4) },
                400: { value: adjustColorLightness(workspaceColors.secondary, 0.2) },
                500: { value: workspaceColors.secondary },
                600: { value: adjustColorLightness(workspaceColors.secondary, -0.1) },
                700: { value: adjustColorLightness(workspaceColors.secondary, -0.2) },
                800: { value: adjustColorLightness(workspaceColors.secondary, -0.3) },
                900: { value: adjustColorLightness(workspaceColors.secondary, -0.4) },
              },
            },
          },
          semanticTokens: {
            colors: {
              brand: {
                solid: { value: '{colors.brand.600}' },
                hover: { value: '{colors.brand.700}' },
                soft: { value: '{colors.brand.50}' },
                contrast: {
                  // DS source of truth: text/icon on the brand fill is always
                  // white (--brand-on-primary), not a per-color contrast calc.
                  value: 'var(--brand-on-primary)',
                },
                fg: {
                  value:
                    primaryContrastColor === '#000000'
                      ? '{colors.brand.800}'
                      : '{colors.brand.700}',
                },
                muted: { value: '{colors.brand.200}' },
                subtle: { value: '{colors.brand.100}' },
                emphasized: { value: '{colors.brand.300}' },
                focusRing: { value: '{colors.brand.500}' },
              },
              secondary: {
                solid: { value: '{colors.secondary.600}' },
                contrast: {
                  value: secondaryContrastColor,
                },
                fg: {
                  value:
                    secondaryContrastColor === '#000000'
                      ? '{colors.secondary.800}'
                      : '{colors.secondary.700}',
                },
                muted: { value: '{colors.secondary.200}' },
                subtle: { value: '{colors.secondary.100}' },
                emphasized: { value: '{colors.secondary.300}' },
                focusRing: { value: '{colors.secondary.500}' },
              },
            },
          },
        },
      }),
    [
      workspaceColors.primary,
      primaryContrastColor,
      workspaceColors.secondary,
      secondaryContrastColor,
    ],
  );

  const system = useMemo(
    () => createSystem(defaultConfig, dsFoundationConfig, dsRecipesConfig, customConfig),
    [customConfig],
  );

  return system;
}

const staticDefaultSystem = createSystem(
  defaultConfig,
  dsFoundationConfig,
  dsRecipesConfig,
  defineConfig({
    theme: {
      recipes: {
        container: containerRecipe,
      },
    },
  }),
);

export { staticDefaultSystem as defaultSystem };
